/**
 * Custom video player for the media studio stage — play/pause, scrubber,
 * time, mute, styled with CircleIconButton + Slider so it reads as the same
 * product as the image dialog's toolbar instead of the browser's native
 * `<video controls>` chrome (different font, different icon set, no
 * consistent hover/active states — a visible seam next to everything else
 * in the dialog, caught in live design review).
 *
 * eslint-disable no-restricted-syntax: the control bar overlays arbitrary
 * video content, so it needs literal white-on-black regardless of app theme —
 * same exception already used by YouTubeShort.tsx / TikTokVideo.tsx for the
 * same reason (platform-preview media overlays).
 */
/* eslint-disable no-restricted-syntax */
import { PauseIcon, PlayIcon, SpeakerHighIcon, SpeakerXIcon } from '@phosphor-icons/react';
import { useCallback, useEffect, useRef, useState } from 'react';

import { CircleIconButton } from '@/components/ui/circle-icon-button';
import { Slider } from '@/components/ui/slider';
import { cn } from '@/lib/utils';

interface MediaStudioVideoPlayerProps {
  src: string;
  poster?: string;
  ariaLabel: string;
  onLoadedData?: () => void;
  onError?: () => void;
  /** Sizing/fit classes applied to the <video> element itself. */
  className?: string;
}

function formatTime(seconds: number): string {
  if (!Number.isFinite(seconds) || seconds < 0) return '0:00';
  const m = Math.floor(seconds / 60);
  const s = Math.floor(seconds % 60);
  return `${m}:${s.toString().padStart(2, '0')}`;
}

export function MediaStudioVideoPlayer({
  src,
  poster,
  ariaLabel,
  onLoadedData,
  onError,
  className,
}: MediaStudioVideoPlayerProps) {
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const wrapRef = useRef<HTMLDivElement | null>(null);
  const [controlsBox, setControlsBox] = useState<{
    left: number;
    width: number;
    bottom: number;
  } | null>(null);
  // Assume paused until told otherwise — autoplay can be silently blocked
  // (backgrounded tab, browser policy), and a hardcoded `true` here left the
  // Pause icon showing while the video sat motionless on frame 1.
  const [isPlaying, setIsPlaying] = useState(false);
  const [isMuted, setIsMuted] = useState(true);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  // While the user is dragging the scrubber, ignore the video's own
  // timeupdate events — otherwise playback fights the drag and the thumb
  // jumps back toward wherever the video actually is.
  const isScrubbingRef = useRef(false);

  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;

    const handleTimeUpdate = () => {
      if (!isScrubbingRef.current) setCurrentTime(video.currentTime);
    };
    const handleLoadedMetadata = () => setDuration(video.duration || 0);
    const handlePlay = () => setIsPlaying(true);
    const handlePause = () => setIsPlaying(false);

    video.addEventListener('timeupdate', handleTimeUpdate);
    video.addEventListener('loadedmetadata', handleLoadedMetadata);
    video.addEventListener('play', handlePlay);
    video.addEventListener('pause', handlePause);
    // Sync immediately — autoplay may already be mid-flight (or blocked)
    // by the time this effect runs, so don't wait for the next play/pause
    // event to correct an initial guess.
    setIsPlaying(!video.paused);
    if (video.duration) setDuration(video.duration);
    return () => {
      video.removeEventListener('timeupdate', handleTimeUpdate);
      video.removeEventListener('loadedmetadata', handleLoadedMetadata);
      video.removeEventListener('play', handlePlay);
      video.removeEventListener('pause', handlePause);
    };
  }, [src]);

  const syncControlsBox = useCallback(() => {
    const wrap = wrapRef.current;
    const video = videoRef.current;
    if (!wrap || !video) return;
    const wrapRect = wrap.getBoundingClientRect();
    const videoRect = video.getBoundingClientRect();
    if (videoRect.width === 0 || videoRect.height === 0) return;
    setControlsBox({
      left: videoRect.left - wrapRect.left,
      width: videoRect.width,
      bottom: wrapRect.bottom - videoRect.bottom,
    });
  }, []);

  useEffect(() => {
    syncControlsBox();
    const video = videoRef.current;
    const wrap = wrapRef.current;
    if (!video || !wrap) return;
    video.addEventListener('loadedmetadata', syncControlsBox);
    video.addEventListener('loadeddata', syncControlsBox);
    window.addEventListener('resize', syncControlsBox);
    let observer: ResizeObserver | undefined;
    if (typeof ResizeObserver !== 'undefined') {
      observer = new ResizeObserver(syncControlsBox);
      observer.observe(wrap);
      observer.observe(video);
    }
    return () => {
      video.removeEventListener('loadedmetadata', syncControlsBox);
      video.removeEventListener('loadeddata', syncControlsBox);
      window.removeEventListener('resize', syncControlsBox);
      observer?.disconnect();
    };
  }, [src, syncControlsBox]);

  const togglePlay = () => {
    const video = videoRef.current;
    if (!video) return;
    if (video.paused) void video.play();
    else video.pause();
  };

  const toggleMute = () => {
    const video = videoRef.current;
    if (!video) return;
    video.muted = !video.muted;
    setIsMuted(video.muted);
  };

  return (
    // self-stretch (not flex-1, not inline-flex) — the stage is a ROW-direction
    // flex container (no flex-col) with `items-center`, so `flex-1` sizes the
    // MAIN axis (width) here, not height — the wrapper's height stayed
    // content-based (auto) regardless, and since a plain <div> doesn't get
    // the replaced-element special-casing that let the *direct* <video> child
    // resolve `max-h-full` correctly pre-refactor, its height collapsed into
    // the same circular dependency as the inline-flex attempt (video render-
    // ed at full natural resolution, 1988px in testing, wrapper matched it).
    // `self-stretch` overrides the container's `items-center` for just this
    // item and fills the flex line's cross size directly — an unambiguous
    // flex-spec sizing mechanism, not a percentage resolution, so it isn't
    // subject to the same footgun. Width is left alone: shrink-to-fit width
    // resolves top-down against the stage's available width regardless of
    // flex/replaced-element rules, so it was never the broken axis.
    <div
      ref={wrapRef}
      className="relative flex items-end justify-center self-stretch md:items-center"
    >
      <video
        ref={videoRef}
        src={src}
        poster={poster}
        autoPlay
        muted
        loop
        playsInline
        disablePictureInPicture
        aria-label={ariaLabel}
        onLoadedData={onLoadedData}
        onError={onError}
        className={className}
      />
      <div
        role="group"
        aria-label="Video controls"
        style={
          controlsBox
            ? {
                left: controlsBox.left,
                width: controlsBox.width,
                bottom: controlsBox.bottom,
                right: 'auto',
              }
            : undefined
        }
        className={cn(
          'absolute inset-x-0 bottom-0 flex items-center gap-2',
          'bg-gradient-to-t from-black/70 via-black/35 to-transparent px-3 pb-2.5 pt-8',
        )}
      >
        <CircleIconButton
          size="sm"
          ariaLabel={isPlaying ? 'Pause' : 'Play'}
          onClick={togglePlay}
          className="text-white hover:bg-white/15 hover:text-white"
        >
          {isPlaying ? <PauseIcon weight="fill" /> : <PlayIcon weight="fill" />}
        </CircleIconButton>
        <span className="shrink-0 text-detail tabular-nums text-white/90">
          {formatTime(currentTime)} / {formatTime(duration)}
        </span>
        <Slider
          value={[currentTime]}
          max={duration || 1}
          step={0.1}
          onValueChange={([next]) => {
            isScrubbingRef.current = true;
            setCurrentTime(next);
            if (videoRef.current) videoRef.current.currentTime = next;
          }}
          onValueCommit={() => {
            isScrubbingRef.current = false;
          }}
          trackClassName="bg-white/25"
          rangeClassName="bg-white"
          thumbClassName="border-white bg-white"
          className="mx-1 flex-1"
          aria-label="Seek"
        />
        <CircleIconButton
          size="sm"
          ariaLabel={isMuted ? 'Unmute' : 'Mute'}
          onClick={toggleMute}
          className="text-white hover:bg-white/15 hover:text-white"
        >
          {isMuted ? <SpeakerXIcon weight="regular" /> : <SpeakerHighIcon weight="regular" />}
        </CircleIconButton>
      </div>
    </div>
  );
}
